// Stage 90: Parallax slot polling hook.
//
// Polls /api/parallax/current-slot at 1500 ms intervals while mounted.
// Cancels on unmount. No accumulation, no history — the inspector reflects
// the live current slot only (TINS §2.4 + §2.5).
//
// Polling cadence rationale (TINS §1.3): Parallax fires on mood-shift
// events with ~1.5–4 s of inference latency. A 1500 ms poll is fast enough
// that a freshly-written slot is visible within ~1.5 s of the inference
// completing, slow enough that even with 10 operators each watching the
// inspector this is just 20 calls/sec to the worker (trivial).

import { useEffect, useState } from 'react';
import { parallaxCurrentSlot, type CupSlot } from '../api/parallax';

const POLL_INTERVAL_MS = 1500;

export interface UseParallaxSlot {
  slot: CupSlot | null;
  loading: boolean;
  error: string | null;
}

export function useParallaxSlot(
  conversationId: string | null,
): UseParallaxSlot {
  const [slot, setSlot] = useState<CupSlot | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setSlot(null);
      setError(null);
      return;
    }
    let active = true;
    const poll = async () => {
      setLoading(true);
      try {
        const res = await parallaxCurrentSlot(conversationId);
        if (!active) return;
        setSlot(res.slot);
        setError(null);
      } catch (e) {
        if (!active) return;
        setError(e instanceof Error ? e.message : 'parallax poll failed');
      } finally {
        if (active) setLoading(false);
      }
    };
    void poll();
    const id = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [conversationId]);

  return { slot, loading, error };
}
